import React, { useState } from 'react';
import { connect } from 'react-redux';

import { getTranslatedString } from 'helpers/i18n';
import { noticeTemplateFormModes } from 'helpers/constants';
import { sortAlphaNum } from 'helpers/utilities';

import { deleteNoticeTemplate } from 'actions/landlordNoticesActions';

import { AntdButton as Button } from 'ui-library/components/Button/Button';
import Col from 'components/ui/Col';
import Spacer from 'components/ui/Spacer';
import Row from 'components/ui/Row';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerFooter from 'components/ui/DrawerFooter';

import { LegalNotice, NoticeOfEntryNotice } from '../constants';
import CreateNoticeTemplate from './CreateNoticeTemplate';
import EditNoticeTemplate from './EditNoticeTemplate';
import styled from 'styled-components';

const StyledSectionContainer = styled.div`
  margin-bottom: 32px;
`;

const StyledTitle = styled.span`
  font-size: 18px;
  font-family: 'Circular-Bold';
`;

const StyledTemplateName = styled.span`
  font-size: 14px;
  font-family: 'Circular-Bold';
`;

const StyledColMb8 = styled(Col)`
  margin-bottom: 8px;
`;

const styles = {
  emptyList: {
    fontSize: '18px',
    textAlign: 'center',
    padding: '15px',
    color: 'rgba(0,0,0,0.5)',
  },
};

const ManageTemplates = ({ handleClose, noticeTemplates, onSubmissionComplete }) => {
  const [mode, setMode] = useState(null);
  const [templateDetails, setTemplateDetails] = useState({});

  const addNewTemplate = () => {
    setTemplateDetails({});
    setMode(noticeTemplateFormModes.CREATE);
  };

  const loadTemplateForEdit = (templateId) => {
    const templateDetails = noticeTemplates.find(
      (noticeTemplate) => noticeTemplate.id === templateId
    );
    if (templateDetails) {
      if ([LegalNotice, NoticeOfEntryNotice].includes(templateDetails.notice_type)) {
        templateDetails.delivery = 'single_unit';
      }

      setTemplateDetails(templateDetails);
      setMode(noticeTemplateFormModes.UPDATE);
    }
  };

  const closeTemplate = () => {
    setTemplateDetails({});
    setMode(null);
  };

  const renderCreate = () => {
    return (
      <CreateNoticeTemplate
        handleClose={closeTemplate}
        onSubmissionComplete={onSubmissionComplete}
      />
    );
  };

  const renderEdit = () => {
    return (
      <EditNoticeTemplate
        noticeTemplateDetails={templateDetails}
        handleClose={closeTemplate}
        onSubmissionComplete={onSubmissionComplete}
      />
    );
  };

  const renderManager = () => {
    const renderList = () => {
      if (noticeTemplates.length === 0) {
        return (
          <div style={styles.emptyList}>
            {getTranslatedString('landlordPortal.noticeTemplateForm.noSavedTemplates')}
          </div>
        );
      }
      const sortedTemplates = noticeTemplates.sort((a, b) => sortAlphaNum(a['label'], b['label']));

      return sortedTemplates.map((noticeTemplate, i) => {
        return (
          <Spacer mb={1}>
            <Row gutter={16} key={`nt-${noticeTemplate.id}`}>
              <Col span={20}>
                <StyledTemplateName>{noticeTemplate.name}</StyledTemplateName>
              </Col>
              <Col span={4}>
                <Button
                  type="link"
                  size="small"
                  label={getTranslatedString('landlordPortal.noticeTemplateForm.view')}
                  onClick={() => loadTemplateForEdit(noticeTemplate.id)}
                />
              </Col>
            </Row>
          </Spacer>
        );
      });
    };

    return (
      <React.Fragment>
        <DrawerHeader
          onClose={handleClose}
          title={getTranslatedString('landlordPortal.addNoticeDialog.manageTemplates')}
        />
        <DrawerBody>
          <StyledSectionContainer>
            <Spacer mb={2}>
              <Row gutter={16}>
                <StyledColMb8 span={18}>
                  <StyledTitle>
                    {getTranslatedString('landlordPortal.noticeTemplateForm.savedTemplates')}
                  </StyledTitle>
                </StyledColMb8>
                <StyledColMb8 span={4}>
                  <Button
                    type="primary"
                    onClick={addNewTemplate}
                    label={getTranslatedString('landlordPortal.noticeTemplateForm.newButton')}
                    size="default"
                  />
                </StyledColMb8>
              </Row>
            </Spacer>

            <Spacer mb={2} />

            {renderList()}
          </StyledSectionContainer>
        </DrawerBody>
        <DrawerFooter noAction={true} />
      </React.Fragment>
    );
  };

  const renderFunc =
    {
      [noticeTemplateFormModes.CREATE]: renderCreate,
      [noticeTemplateFormModes.UPDATE]: renderEdit,
    }[mode] || renderManager;

  return renderFunc();
};

export default connect(
  (store) => ({
    noticeTemplates: store.landlordNotices.notice_templates,
  }),
  {
    deleteNoticeTemplate,
  }
)(ManageTemplates);
